import { Controller } from "@hotwired/stimulus";

// Generic controller for managing bulk selection checkboxes.
// Handles select all/deselect all and enables/disables control elements.
//
// Targets:
//   - selectAll: Checkbox to select/deselect all (optional)
//   - checkbox: Individual row checkboxes
//   - control: Elements to enable/disable based on selection (buttons, etc.)
//
// Values:
//   - restoreSelections (Boolean, default: true): When inside a turbo-frame,
//     selections are preserved across re-renders. Set to false after bulk
//     actions to clear selections.
//
// Usage:
//   <div data-controller="bulk-actions" data-bulk-actions-restore-selections-value="true">
//     <button data-bulk-actions-target="control" disabled>Actions</button>
//     <table>
//       <thead>
//         <tr>
//           <th><input type="checkbox" data-bulk-actions-target="selectAll" data-action="change->bulk-actions#selectAll"></th>
//         </tr>
//       </thead>
//       <tbody>
//         <tr>
//           <td><input type="checkbox" name="ids[]" value="123" data-bulk-actions-target="checkbox" data-action="change->bulk-actions#checkboxChanged"></td>
//         </tr>
//       </tbody>
//     </table>
//   </div>
//
export default class extends Controller {
  static targets = ["selectAll", "checkbox", "control"];
  static values = { restoreSelections: { type: Boolean, default: true } };

  connect() {
    this.frame = this.element.closest("turbo-frame");

    if (this.restoreSelectionsValue) {
      this.restoreSelections();
    } else {
      this.clearSavedSelections();
    }

    this.updateControls();
  }

  saveSelections() {
    if (!this.frame) return;
    const ids = this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);
    this.frame.dataset.savedSelections = JSON.stringify(ids);
  }

  restoreSelections() {
    if (!this.frame || !this.frame.dataset.savedSelections) return;

    const savedIds = JSON.parse(this.frame.dataset.savedSelections);
    if (savedIds.length === 0) return;

    this.checkboxTargets.forEach((cb) => {
      cb.checked = savedIds.includes(cb.value);
    });
    this.updateSelectAllState();
  }

  selectAll(event) {
    const checked = event.target.checked;
    this.checkboxTargets.forEach((cb) => (cb.checked = checked));
    this.saveSelections();
    this.updateControls();
  }

  checkboxChanged() {
    this.updateSelectAllState();
    this.saveSelections();
    this.updateControls();
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return;

    const total = this.checkboxTargets.length;
    const checked = this.checkboxTargets.filter((cb) => cb.checked).length;

    if (checked === 0) {
      this.selectAllTarget.checked = false;
      this.selectAllTarget.indeterminate = false;
    } else if (checked === total) {
      this.selectAllTarget.checked = true;
      this.selectAllTarget.indeterminate = false;
    } else {
      this.selectAllTarget.checked = false;
      this.selectAllTarget.indeterminate = true;
    }
  }

  updateControls() {
    const hasSelection = this.checkboxTargets.some((cb) => cb.checked);
    this.controlTargets.forEach((el) => (el.disabled = !hasSelection));
  }

  clearSavedSelections() {
    if (this.frame) {
      delete this.frame.dataset.savedSelections;
    }
  }

  reset() {
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false;
      this.selectAllTarget.indeterminate = false;
    }
    this.checkboxTargets.forEach((cb) => (cb.checked = false));
    this.clearSavedSelections();
    this.updateControls();
  }
}
